import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    LayoutDashboard, Briefcase, MessageSquare, CalendarDays, User, LogOut, Bell,
    Building2, Users, Shield, PlusCircle, Search, ShieldCheck, BarChart3, Bookmark
} from "lucide-react";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
    DropdownMenuSeparator, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";

const proLinks = [
    { to: "/app/opportunita", label: "Opportunità", icon: LayoutDashboard },
    { to: "/app/salvate", label: "Salvate", icon: Bookmark },
    { to: "/app/candidature", label: "Candidature", icon: Briefcase },
    { to: "/app/chat", label: "Chat", icon: MessageSquare },
    { to: "/app/calendario", label: "Calendario", icon: CalendarDays },
    { to: "/app/profilo", label: "Profilo", icon: User },
];
const facLinks = [
    { to: "/app/annunci", label: "Annunci", icon: LayoutDashboard },
    { to: "/app/candidature-ricevute", label: "Candidature", icon: Users },
    { to: "/app/cerca-professionisti", label: "Cerca", icon: Search },
    { to: "/app/statistiche", label: "Statistiche", icon: BarChart3 },
    { to: "/app/chat", label: "Chat", icon: MessageSquare },
    { to: "/app/calendario", label: "Calendario", icon: CalendarDays },
    { to: "/app/struttura", label: "Struttura", icon: Building2 },
];
const adminLinks = [
    { to: "/app/admin", label: "Dashboard", icon: Shield },
    { to: "/app/admin/verifiche", label: "Verifiche", icon: ShieldCheck },
];

export default function DashboardLayout() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const [notif, setNotif] = useState({ items: [], unread: 0 });

    const loadNotif = async () => {
        try {
            const { data } = await api.get("/notifications");
            setNotif(data.data);
        } catch {}
    };
    useEffect(() => {
        loadNotif();
        const t = setInterval(loadNotif, 15000);
        return () => clearInterval(t);
    }, []);

    if (!user) return null;
    const links = user.role === "professional" ? proLinks
                : user.role === "facility" ? facLinks : adminLinks;

    const doLogout = async () => { await logout(); nav("/"); };
    const markAllRead = async () => {
        await api.post("/notifications/read-all");
        loadNotif();
    };

    return (
        <div className="min-h-screen bg-[#F9F9F7] flex">
            {/* Sidebar */}
            <aside className="hidden md:flex w-64 flex-col border-r border-[#E5E5E0] bg-white">
                <Link to="/" className="h-16 px-6 flex items-center gap-2 border-b border-[#E5E5E0]">
                    <div className="h-8 w-8 rounded-md bg-[#1C3F39] grid place-items-center text-white font-display font-bold">H</div>
                    <span className="font-display text-lg font-bold tracking-tight">HealthLink</span>
                </Link>
                <nav className="flex-1 p-4 space-y-1">
                    {links.map((l) => (
                        <NavLink key={l.to} to={l.to} end
                            data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                                    isActive ? "bg-[#EEF3F1] text-[#1C3F39] font-semibold" : "text-[#5C5C5C] hover:bg-[#F9F9F7]"
                                }`
                            }>
                            <l.icon className="w-4 h-4" /> {l.label}
                        </NavLink>
                    ))}
                    {user.role === "facility" && (
                        <NavLink to="/app/annunci/nuovo"
                            data-testid="nav-new-job"
                            className="mt-4 flex items-center gap-2 px-3 py-2 rounded-md text-sm bg-[#1C3F39] text-white hover:bg-[#2A5C53] transition-colors">
                            <PlusCircle className="w-4 h-4" /> Nuovo annuncio
                        </NavLink>
                    )}
                </nav>
                <div className="p-4 border-t border-[#E5E5E0]">
                    <div className="text-xs text-[#5C5C5C]">Accesso come</div>
                    <div className="text-sm font-semibold truncate" data-testid="sidebar-user-name">{user.full_name}</div>
                    <div className="text-xs text-[#5C5C5C] capitalize">{user.role}</div>
                </div>
            </aside>

            {/* Main */}
            <div className="flex-1 flex flex-col">
                <header className="h-16 border-b border-[#E5E5E0] bg-white/95 backdrop-blur px-6 flex items-center justify-between sticky top-0 z-30">
                    <div className="md:hidden font-display font-bold">HealthLink</div>
                    <div className="hidden md:block" />
                    <div className="flex items-center gap-2">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
                                    <Bell className="w-5 h-5" />
                                    {notif.unread > 0 && (
                                        <span className="absolute top-1 right-1 h-4 min-w-[16px] px-1 rounded-full bg-[#D96C5B] text-white text-[10px] font-bold grid place-items-center">
                                            {notif.unread}
                                        </span>
                                    )}
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-80">
                                <DropdownMenuLabel className="flex justify-between items-center">
                                    <span>Notifiche</span>
                                    {notif.unread > 0 && (
                                        <button onClick={markAllRead} className="text-xs text-[#1C3F39] underline" data-testid="mark-all-read">
                                            Segna tutte lette
                                        </button>
                                    )}
                                </DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <div className="max-h-96 overflow-y-auto">
                                    {notif.items.length === 0 && (
                                        <div className="p-4 text-sm text-[#5C5C5C]">Nessuna notifica</div>
                                    )}
                                    {notif.items.slice(0, 20).map((n) => (
                                        <DropdownMenuItem key={n.id} className="flex-col items-start gap-1 py-3">
                                            <div className="flex items-center gap-2 w-full">
                                                {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-[#D96C5B]"></span>}
                                                <span className="font-medium text-sm">{n.title}</span>
                                            </div>
                                            <span className="text-xs text-[#5C5C5C]">{n.body}</span>
                                        </DropdownMenuItem>
                                    ))}
                                </div>
                            </DropdownMenuContent>
                        </DropdownMenu>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" className="gap-2" data-testid="user-menu-btn">
                                    <div className="w-7 h-7 rounded-full bg-[#1C3F39] text-white grid place-items-center text-xs font-bold">
                                        {user.full_name?.[0]?.toUpperCase()}
                                    </div>
                                    <span className="hidden md:inline text-sm">{user.full_name}</span>
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuLabel>{user.email}</DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={doLogout} data-testid="logout-btn">
                                    <LogOut className="w-4 h-4 mr-2" /> Esci
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </header>
                <main className="flex-1 p-6 md:p-8">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
